// src/components/CommissionsTable.jsx
// ============================================================================
// BLOCK COMM.TABLE.0 — Commissions Table (ProdMgrs-style search/sort/running)
// Behavior:
//  1) Paint the first 50 rows; fetch more details only when requested
//  2) Filter by search text (includes date tokens)
//  3) Sort
//  4) Preserve server-calculated running totals across all pages
//  5) Paginate client-side
// ============================================================================

import React from 'react';
import PropTypes from 'prop-types';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import axios from '../axiosClient.js';
import CommissionsHeader from './CommissionsHeader.jsx';
import CommissionsRow from './CommissionsRow.jsx';

const ROWS_PER_PAGE_OPTIONS = [50, 250, 1000];
const FIRST_PAGE_SIZE = 50;
const FETCH_BATCH_SIZE = 2500;

function toNum(v) {
  if (v === null || v === undefined) return 0;
  if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
  if (typeof v === 'string') {
    const cleaned = v.replace(/[$,]/g, '').trim();
    if (!cleaned) return 0;
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : 0;
  }
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function cmp(a, b) {
  if (a === b) return 0;
  if (a === null || a === undefined) return -1;
  if (b === null || b === undefined) return 1;
  return a < b ? -1 : 1;
}

function valueForSort(row, sortBy) {
  if (!row) return null;

  if (sortBy === 'id') return toNum(row.id);
  if (sortBy === 'InvoiceDate') return String(row.InvoiceDate ?? '');
  if (sortBy === 'InvoiceNo') return String(row.InvoiceNo ?? '');
  if (sortBy === 'ProductGroup') return String(row.ProductGroup ?? '');
  if (sortBy === 'NetSale') return toNum(row.NetSale);
  if (sortBy === 'CommissionRate') return toNum(row.CommissionRate ?? row.TriggeredTiers);
  if (sortBy === 'LineCommissions') return toNum(row.LineCommissions);

  return row[sortBy];
}

function sortRows(rows, sortBy, sortDir) {
  const dir = sortDir === 'desc' ? -1 : 1;
  const safe = Array.isArray(rows) ? rows : [];
  const decorated = safe.map((r, i) => ({ r, i }));

  decorated.sort((x, y) => {
    const ax = valueForSort(x.r, sortBy);
    const ay = valueForSort(y.r, sortBy);

    const c = cmp(ax, ay);
    if (c !== 0) return c * dir;
    return x.i - y.i;
  });

  return decorated.map((d) => d.r);
}

// ============================================================================
// BLOCK PMGR.COMM.SEARCH.0 — Global client-side search (robust date tokens)
// Fix:
//  - Avoid Date.parse quirks for inputs like "01/07/26"
//  - Generate tokens for ISO + US slash formats consistently
// ============================================================================

function parseLooseDate(value) {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const s = String(value).trim();
  if (!s) {
    return null;
  }

  // ISO: YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss...
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    const y = Number(iso[1]);
    const m = Number(iso[2]);
    const d = Number(iso[3]);
    const dt = new Date(y, m - 1, d);
    return Number.isNaN(dt.getTime()) ? null : dt;
  }

  // US slashes: M/D/YY, MM/DD/YY, M/D/YYYY, MM/DD/YYYY
  const us = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})/);
  if (us) {
    const month = Number(us[1]);
    const day = Number(us[2]);

    let year = Number(us[3]);
    if (us[3].length === 2) {
      // interpret 00-99 as 2000-2099 (matches your use case: 26 => 2026)
      year = 2000 + year;
    }

    const dt = new Date(year, month - 1, day);
    return Number.isNaN(dt.getTime()) ? null : dt;
  }

  // Fallback: try native parsing (for full ISO timestamps, etc.)
  const dt = new Date(s);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function toSearchableDateTokens(value) {
  const d = parseLooseDate(value);
  if (!d) {
    return [];
  }

  const month = d.getMonth() + 1;
  const day = d.getDate();
  const year2 = String(d.getFullYear()).slice(-2);
  const year4 = String(d.getFullYear());

  const mm = String(month).padStart(2, '0');
  const dd = String(day).padStart(2, '0');

  return [
    `${month}/${day}/${year2}`,
    `${mm}/${dd}/${year2}`,
    `${month}/${day}/${year4}`,
    `${mm}/${dd}/${year4}`,
    `${year4}-${mm}-${dd}`,
  ];
}

// ============================================================================
// BLOCK COMM.TABLE.SEARCH.1 — Normalize row to search text (PO + PartNo added)
// ============================================================================

function normalizeCommissionRowToSearchText(r) {
  const invoiceDateTokens = toSearchableDateTokens(r?.InvoiceDate);

  // Reps might be stored in multiple fields; include both “Rep” and numbered reps
  const reps = [
    r?.Rep,
    r?.['2ndRep'],
    r?.['3rdRep'],
    r?.['4thRep'],
    r?.['5thRep'],
    r?.RepName,
  ].filter(Boolean);

  // Customer PO variants (you said “Customer PO” specifically)
  const customerPoVariants = [
    r?.CustomerPONo,
    r?.CustomerPO,
    r?.CustomerPONo,
    r?.PONo,
    r?.PO,
    r?.PoNumber,
  ].filter(Boolean);

  // Part number / item variants (covers “partno too” without needing exact name)
  const partNoVariants = [
    r?.PartNo,
    r?.PartNumber,
    r?.PartNo1,
    r?.ItemNo,
    r?.ItemNumber,
    r?.SKU,
    r?.Sku,
    r?.ProductCode,
    r?.ProductId,
    r?.Item, // keep existing
    r?.ItemDesc,
    r?.Description,
  ].filter(Boolean);

  const parts = [
    // Invoice identifiers
    r?.InvoiceNo,
    r?.InvoiceDate,
    ...invoiceDateTokens,

    // Customer identifiers
    r?.Customer,
    r?.CustomerName,
    r?.CustomerNo,

    // PO / order identifiers (added)
    ...customerPoVariants,
    r?.SalesOrderNo,
    r?.OrderNo,
    r?.SO,
    r?.SONo,

    // Tracking identifiers
    r?.TrackingID,
    r?.Tracking,
    r?.TrackingNo,
    r?.MstrTrackingNo,

    // Location
    r?.State,
    r?.ZipCode,
    r?.Zip,
    r?.AreaCode,
    r?.City,

    // Reps
    ...reps,
    r?.Reps, // if you build a combined string elsewhere

    // Item / PartNo (added)
    ...partNoVariants,

    // Product Group / Category
    r?.ProductGroup,
    r?.Category,

    // Numbers (helps searching amounts too)
    r?.Quantity,
    r?.UnitPrice,
    r?.Sale,
    r?.StandardCost,
    r?.BaseCost,
    r?.ContractFee,
    r?.NetSale,

    // Commission fields
    r?.CommissionRate,
    r?.TriggeredTiers,
    r?.LineCommissions,
    r?.TotalCommission,

    // Running totals (if you want to find a specific total)
    r?.RunningTotal,
    r?.RunningNetTotal,
    r?.RunningCommissionsTotal,
  ];

  return parts
    .map((v) => (v === null || v === undefined ? '' : String(v)))
    .join(' ')
    .toLowerCase();
}

function CommissionsTable({ selectedRep, selectedYear, selectedMonth, onInitialLoad }) {
  const sessionRef = React.useRef(null);
  const [reload, setReload] = React.useState(0);
  const [trackingWarning, setTrackingWarning] = React.useState('');
  const [trackingPending, setTrackingPending] = React.useState(0);
  const [rawLines, setRawLines] = React.useState([]);

  const [rows, setRows] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [totalRows, setTotalRows] = React.useState(0);

  const [loading, setLoading] = React.useState(false);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [error, setError] = React.useState('');

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(ROWS_PER_PAGE_OPTIONS[0]);

  // Search + sorting state
  const [searchText, setSearchText] = React.useState('');
  const [sortBy, setSortBy] = React.useState('id');
  const [sortDir, setSortDir] = React.useState('desc');

  const isRep = React.useMemo(() => !selectedRep.includes('contract-accounts'), [selectedRep]);

  const onSort = React.useCallback(
    (col) => {
      setPage(0);

      if (sortBy !== col) {
        setSortBy(col);
        setSortDir('asc');
        return;
      }
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    },
    [sortBy]
  );

  // A selection owns its requests. Aborting it prevents late results being appended.
  React.useEffect(() => {
    const session = {
      controller: new AbortController(),
      tracking: Promise.resolve(),
      fetching: false,
      readyTimer: null,
    };
    sessionRef.current = session;
    const active = () => !session.controller.signal.aborted;
    const url = `/api/commissions/${encodeURIComponent(selectedRep)}/${selectedYear}`;
    session.fetch = (offset, limit, includeTracking) =>
      axios.get(url, {
        timeout: 60000,
        signal: session.controller.signal,
        params: { month: selectedMonth, limit, offset, includeTracking },
      });

    // One tracking request at a time; it never holds up the detail page loader.
    session.enqueueTracking = (offset, limit) => {
      setTrackingPending((count) => count + 1);
      session.tracking = session.tracking.then(async () => {
        if (!active()) return;
        try {
          // Let the browser paint the core rows before starting auxiliary work.
          await new Promise((resolve) => setTimeout(resolve, 100));
          if (!active()) return;
          const res = await session.fetch(offset, limit, true);
          if (!active()) return;
          const tracking = new Map(
            (res.data.commissions || []).map((row) => [row.id, row.TrackingID])
          );
          setRawLines((previous) =>
            previous.map((row) =>
              tracking.has(row.id) ? { ...row, TrackingID: tracking.get(row.id) } : row
            )
          );
        } catch (err) {
          if (active())
            setTrackingWarning(
              'Some tracking details could not load. Sales and commissions remain available. Refresh to retry tracking.'
            );
        } finally {
          if (active()) setTrackingPending((count) => Math.max(0, count - 1));
        }
      });
    };

    setRawLines([]);
    setRows([]);
    setTotal(0);
    setTotalRows(0);
    setPage(0);
    setError('');
    setTrackingWarning('');
    setTrackingPending(0);
    setLoadingMore(false);
    setLoading(Boolean(selectedRep && selectedYear));

    if (selectedRep && selectedYear) {
      session
        .fetch(0, FIRST_PAGE_SIZE, false)
        .then((res) => {
          if (!active()) return;
          const lines = Array.isArray(res?.data?.commissions) ? res.data.commissions : [];
          setRawLines(lines);
          setTotalRows(Number(res?.data?.total) || lines.length);
          if (lines.length) session.enqueueTracking(0, lines.length);
        })
        .catch((err) => {
          if (active())
            setError(err?.response?.data?.error || err?.message || 'Failed to load commissions');
        })
        .finally(() => {
          if (!active()) return;
          setLoading(false);
          // Release summaries after the first attempt, independently of tracking/more pages.
          session.readyTimer = setTimeout(() => {
            if (active()) onInitialLoad?.();
          }, 50);
        });
    }
    return () => {
      session.controller.abort();
      clearTimeout(session.readyTimer);
    };
  }, [selectedRep, selectedYear, selectedMonth, reload, onInitialLoad]);

  const canFetchMore = rawLines.length < totalRows;

  const handleFetchMore = React.useCallback(async () => {
    const session = sessionRef.current;
    if (loading || loadingMore || !canFetchMore || !session || session.fetching) return;
    session.fetching = true;
    setLoadingMore(true);
    setError('');
    const offset = rawLines.length;
    try {
      const res = await session.fetch(offset, FETCH_BATCH_SIZE, false);
      if (session.controller.signal.aborted) return;
      const nextLines = Array.isArray(res?.data?.commissions) ? res.data.commissions : [];
      if (!nextLines.length) {
        // Do not loop forever if the source changed between page requests.
        setTotalRows(offset);
        return;
      }
      setRawLines((previous) => [...previous, ...nextLines]);
      setTotalRows(Number(res?.data?.total) || offset + nextLines.length);
      session.enqueueTracking(offset, nextLines.length);
    } catch (err) {
      if (!session.controller.signal.aborted) {
        setError(
          err?.response?.data?.error ||
            err?.message ||
            'Background loading paused. Retry to continue.'
        );
      }
    } finally {
      session.fetching = false;
      if (!session.controller.signal.aborted) setLoadingMore(false);
    }
  }, [loading, loadingMore, canFetchMore, rawLines.length]);

  // Filter -> sort (display-only; do NOT recompute running totals client-side)
  React.useEffect(() => {
    const q = String(searchText || '')
      .trim()
      .toLowerCase();

    const filtered = !q
      ? rawLines
      : (rawLines || []).filter((r) => normalizeCommissionRowToSearchText(r).includes(q));

    const sorted = sortRows(filtered, sortBy, sortDir);

    setRows(sorted);
    setTotal(sorted.length);
  }, [rawLines, searchText, sortBy, sortDir]);

  React.useEffect(() => setPage(0), [searchText, rowsPerPage]);

  const pageRows = React.useMemo(() => {
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    return rows.slice(start, end);
  }, [rows, page, rowsPerPage]);

  return (
    <Paper elevation={2} sx={{ padding: 2 }}>
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        sx={{ mb: 1 }}
      >
        <Typography variant="h6">Line Detail</Typography>

        {/* ================================================================= */}
        {/* BLOCK COMM.TABLE.SEARCH.0 — Global client-side search              */}
        {/* ================================================================= */}
        <TextField
          size="small"
          label="Search"
          placeholder="Invoice, Customer, Item, City, State, Tracking, Date (MM/DD/YY)…"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          sx={{ minWidth: 320 }}
        />
      </Stack>

      {error ? <Alert severity="error">{error}</Alert> : null}
      {trackingWarning ? <Alert severity="warning">{trackingWarning}</Alert> : null}

      {!loading ? (
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
          <Button
            variant="outlined"
            onClick={
              rawLines.length === 0 && error
                ? () => setReload((value) => value + 1)
                : handleFetchMore
            }
            disabled={loadingMore || (!canFetchMore && !(error && rawLines.length === 0))}
          >
            {loadingMore ? 'Loading more...' : error ? 'Retry loading' : 'Load 2,500 more rows'}
          </Button>
          <Typography variant="body2" color="text.secondary">
            Loaded {rawLines.length.toLocaleString()} of {totalRows.toLocaleString()} rows
            {trackingPending > 0 ? ' — tracking loading' : ''}
            {canFetchMore
              ? '. Search and sorting cover loaded rows. Load more as needed; CSV exports the full selection.'
              : ''}
          </Typography>
        </Stack>
      ) : null}

      {loading ? (
        <Stack sx={{ py: 4 }} alignItems="center">
          <CircularProgress />
        </Stack>
      ) : (
        <>
          <TableContainer>
            <Table stickyHeader size="small">
              <CommissionsHeader isRep={isRep} sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <TableBody>
                {pageRows?.length ? (
                  pageRows.map((commission) => (
                    <CommissionsRow key={commission.id} commission={commission} isRep={isRep} />
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={30} sx={{ textAlign: 'center', py: 2 }}>
                      No commissions data set for {selectedRep}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            showFirstButton
            showLastButton
          />
        </>
      )}
    </Paper>
  );
}

CommissionsTable.propTypes = {
  selectedRep: PropTypes.string.isRequired,
  selectedYear: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  selectedMonth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onInitialLoad: PropTypes.func,
};

export default React.memo(CommissionsTable);
